import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thanks — your message has been sent.");
      setForm(initialState);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-white">
      <div className="container-wide">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Contact Us
            </p>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground">
              Send a message
            </h2>
            <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed">
              We at GEES give utmost importance to business ethics, quality
              excellence & reliability. Reach out — our team will respond shortly.
            </p>

            <div className="mt-10 space-y-7">
              <div className="flex gap-4" data-testid="contact-address-europe">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Europe Office
                  </p>
                  <p className="mt-1 text-sm text-foreground leading-relaxed">
                    Level 18, 40 Bank Street,<br />
                    Canary Wharf E14 5NR, London, UK
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-testid="contact-address-uae">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Middle East Office
                  </p>
                  <p className="mt-1 text-sm text-foreground leading-relaxed">
                    Level 17, Office Tower,<br />
                    World Trade Center, Abu Dhabi, UAE
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-testid="contact-phone">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Phone
                  </p>
                  <p className="mt-1 text-sm text-foreground">
                    UK: <a className="hover:text-primary" href="tel:+442035040957">+44 20 3504 0957</a>
                  </p>
                  <p className="text-sm text-foreground">
                    UAE: <a className="hover:text-primary" href="tel:+97126544163">+971 (2) 654 4163</a>
                  </p>
                  <p className="text-sm text-foreground">
                    UAE Mobile: <a className="hover:text-primary" href="tel:+971504135524">+971 50 413 5524</a>
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-testid="contact-email">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Email
                  </p>
                  <a
                    className="mt-1 text-sm text-foreground hover:text-primary"
                    href="mailto:sales@greenearthuk.co.uk"
                  >
                    sales@greenearthuk.co.uk
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-2xl border border-border bg-secondary/30 p-6 sm:p-8 lg:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-foreground">
                    Full name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your name"
                    required
                    data-testid="contact-input-name"
                    className="mt-2 h-11 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-foreground">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    required
                    data-testid="contact-input-email"
                    className="mt-2 h-11 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-foreground">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="Optional"
                    data-testid="contact-input-phone"
                    className="mt-2 h-11 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="subject" className="text-sm font-semibold text-foreground">
                    Subject
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    placeholder="What is this about?"
                    data-testid="contact-input-subject"
                    className="mt-2 h-11 bg-white"
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-sm font-semibold text-foreground">
                  Message <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  required
                  placeholder="Tell us about your project, timeline and how we can help..."
                  data-testid="contact-input-message"
                  className="mt-2 bg-white"
                />
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Your email address will not be published. Required fields are marked *.
              </p>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-6 w-full sm:w-auto bg-primary hover:bg-primary/90 rounded-full h-12 px-8"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
